import { makePersisted } from "@solid-primitives/storage";
import { createSignal, startTransition } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";

export type UIStore = ReturnType<typeof createUIService>;

export type PendingAuthActionType = "prompt" | "upload" | "project" | "integration" | "share";

export type PendingAuthAction = {
  type: PendingAuthActionType;
  data?: unknown;
};

type AuthGateModalState = {
  title: string;
  description?: string;
  pendingAction?: PendingAuthAction;
  /** The feature that triggered the auth gate (used for analytics). */
  feature?: string;
};

type ProjectSwitchConfirmModalState = {
  currentProjectId: string;
  currentProjectName: string;
  newProjectId: string;
  newProjectName: string;
  onSwitchToNew: () => void;
  onStayOnCurrent: () => void;
};

type ProjectSwitchStreamingModalState = {
  targetProjectId: string;
  targetProjectName: string;
  onStopAndSwitch: () => void;
  onWait: () => void;
};

type DisallowedModalPayload = {
  resubmitWithAutoMode: () => void;
  // Omitted/undefined keeps the server-driven entitlement copy; "insufficient_credits"
  // is the client-side low-balance block that downgrades to Automatic instead of upselling.
  reason?: "feature_gate" | "insufficient_credits";
};

type AiLinkWarningModalState = {
  href: string;
};

const createLayoutSubservice = () => {
  const NAV_COLLAPSED = 48;
  const NAV_DEFAULT = 200;
  const NAV_MIN = NAV_DEFAULT; // Floor at default — users can only expand from here
  const NAV_MAX_PERCENTAGE = 0.4; // 40% of window width
  const NAV_MAX_FALLBACK = 500; // Fallback for SSR

  const SIDE_MIN = 300;
  const SIDE_MAX_PERCENTAGE = 0.7; // 70% of window width
  const SIDE_MAX_FALLBACK = 900; // Fallback for SSR
  const SIDE_DEFAULT = 550;
  const SIDE_COLLAPSE_THRESHOLD = 150; // Collapse when dragged below this

  // Responsive breakpoints (matching Tailwind)
  const BREAKPOINT_SM = 640;
  const BREAKPOINT_LG = 1024;

  // Track screen width for responsive behavior
  const [screenWidth, setScreenWidth] = createSignal(isServer ? 1280 : window.innerWidth);

  // Derived breakpoint signals
  const isSmallScreen = () => screenWidth() < BREAKPOINT_SM;
  const isLargeScreen = () => screenWidth() >= BREAKPOINT_LG;

  // Dynamic SIDE_MAX based on window width (70% of viewport)
  const [sideMaxWidth, setSideMaxWidth] = createSignal(
    isServer ? SIDE_MAX_FALLBACK : Math.floor(window.innerWidth * SIDE_MAX_PERCENTAGE)
  );
  // Dynamic NAV_MAX based on window width (40% of viewport)
  const [navMaxWidth, setNavMaxWidth] = createSignal(
    isServer ? NAV_MAX_FALLBACK : Math.floor(window.innerWidth * NAV_MAX_PERCENTAGE)
  );

  // Update max width and screen width on window resize (client-side only)
  // Debounced to prevent excessive updates during active resizing
  if (!isServer) {
    let resizeTimeoutId: ReturnType<typeof setTimeout> | undefined;
    const RESIZE_DEBOUNCE_MS = 150;

    const handleResize = () => {
      // Always update max widths immediately for smooth panel resizing
      setSideMaxWidth(Math.floor(window.innerWidth * SIDE_MAX_PERCENTAGE));
      setNavMaxWidth(Math.floor(window.innerWidth * NAV_MAX_PERCENTAGE));

      // Debounce screenWidth updates to prevent component remounting during resize
      // This is important because isSmallScreen/isLargeScreen depend on screenWidth
      // and Show components that use these will recreate children when they change
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      resizeTimeoutId = setTimeout(() => {
        void startTransition(() => setScreenWidth(window.innerWidth));
        resizeTimeoutId = undefined;
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", handleResize);
  }

  const SIDE_MAX = () => Math.max(SIDE_MIN + 100, sideMaxWidth()); // Ensure max is always > min
  const NAV_MAX = () => Math.max(NAV_MIN + 50, navMaxWidth()); // Ensure max is always > min

  // We don't store the side collapsed information in localStorage, that's determined in the navigation service based on the url
  const [state, setState] = makePersisted(
    createStore({
      navbar: {
        size: 200,
        collapsed: false,
      },
      main: {},
      side: {
        size: 550,
      },
    }),
    {
      storage: isServer ? undefined : localStorage,
      name: "layout-state:v1",
    }
  );

  // URL-based collapse state (no side content in URL)
  const [collapsed, setCollapsed] = createSignal({
    side: true,
  });

  // User-minimized state (side content exists but user dragged to collapse)
  const [minimized, setMinimized] = createSignal({
    side: false,
  });

  // A side page can resolve to nothing (scope-picker outside a chat with content). Only the panel
  // can tell, so it reports here and everything anchored to the panel's edge follows the same width.
  const [sideEmpty, setSideEmpty] = createSignal(false);

  const [userMenuDropdownOpen, setUserMenuDropdownOpen] = createSignal(false);
  const [notificationsOpen, setNotificationsOpen] = createSignal(false);

  const navbarCollapsed = () => state.navbar.collapsed;
  const navbarExpandedSize = () => Math.min(NAV_MAX(), Math.max(NAV_MIN, state.navbar.size));
  const navbarSize = () => (navbarCollapsed() ? NAV_COLLAPSED : navbarExpandedSize());

  // Side is collapsed if URL says no content, OR if user minimized it
  const sideCollapsed = () => collapsed().side;
  const sideMinimized = () => minimized().side;
  const sideExpandedSize = () => state.side.size;
  const sideSize = () => (sideCollapsed() || sideMinimized() || sideEmpty() ? 0 : sideExpandedSize());

  const setSideCollapsed = (isCollapsed: boolean) => {
    setCollapsed((c) => ({ ...c, side: isCollapsed }));
    // When URL shows content, reset minimized state
    if (!isCollapsed) {
      setMinimized((m) => ({ ...m, side: false }));
    }
  };
  const setSideMinimized = (isMinimized: boolean) => setMinimized((m) => ({ ...m, side: isMinimized }));
  const setNavbarCollapsed = (isCollapsed: boolean) => setState("navbar", "collapsed", isCollapsed);
  const resizeNavbar = (size: number) => setState("navbar", "size", Math.min(NAV_MAX(), Math.max(NAV_MIN, size)));
  const resizeSide = (size: number) => setState("side", "size", Math.min(SIDE_MAX(), Math.max(SIDE_MIN, size)));

  return {
    navbarSize,
    navbarCollapsed,
    userMenuDropdownOpen,
    setUserMenuDropdownOpen,
    notificationsOpen,
    setNotificationsOpen,
    sideSize,
    sideExpandedSize,
    sideCollapsed,
    sideMinimized,
    sideEmpty,
    setSideEmpty,
    resizeNavbar,
    resizeSide,
    setNavbarCollapsed,
    setSideCollapsed,
    setSideMinimized,
    SIDE_MIN,
    SIDE_DEFAULT,
    SIDE_COLLAPSE_THRESHOLD,
    NAV_DEFAULT,
    // Responsive breakpoints
    isSmallScreen,
    isLargeScreen,
  };
};

export const createUIService = () => {
  const [state, setState] = createStore({
    creditsGateModal: "",
    chatPermissionsGateModal: false,
    manageLabelsModal: false,
    processingAssetsGateModal: false,
    processingAssetsGateAction: null as null | (() => unknown),
    processingAssetsGateReason: null as null | "structured" | "volume" | "mixed",
    authGateModal: null as null | AuthGateModalState,
    chatAccessDeniedModal: false,
    disallowedModal: null as null | DisallowedModalPayload,
    projectSwitchConfirmModal: null as null | ProjectSwitchConfirmModalState,
    projectSwitchStreamingModal: null as null | ProjectSwitchStreamingModalState,
    aiLinkWarningModal: null as null | AiLinkWarningModalState,
  });

  return {
    layout: createLayoutSubservice(),
    creditsGate: {
      isModalOpen: () => state.creditsGateModal !== "",
      description: () => state.creditsGateModal,
      openModal: (description: string) => setState("creditsGateModal", description),
      closeModal: () => setState("creditsGateModal", ""),
    },
    chatPermissionsGate: {
      isModalOpen: () => state.chatPermissionsGateModal,
      openModal: () => setState("chatPermissionsGateModal", true),
      closeModal: () => setState("chatPermissionsGateModal", false),
    },
    manageLabels: {
      isModalOpen: () => state.manageLabelsModal,
      openModal: () => setState("manageLabelsModal", true),
      closeModal: () => setState("manageLabelsModal", false),
    },
    processingAssetsGate: {
      isModalOpen: () => state.processingAssetsGateModal,
      executeGatedAction: () => state.processingAssetsGateAction?.(),
      getGateReason: () => state.processingAssetsGateReason,
      // oxlint-disable-next-line typescript/no-explicit-any -- Type doesn't matter
      openModal: (action?: () => any, reason?: "structured" | "volume" | "mixed") => {
        setState("processingAssetsGateAction", () => action ?? null);
        setState("processingAssetsGateReason", reason ?? null);
        setState("processingAssetsGateModal", true);
      },
      closeModal: () => {
        setState("processingAssetsGateAction", null);
        setState("processingAssetsGateReason", null);
        setState("processingAssetsGateModal", false);
      },
    },
    authGate: {
      isModalOpen: () => state.authGateModal !== null,
      state: () => state.authGateModal,
      openModal: (options: AuthGateModalState) => setState("authGateModal", options),
      closeModal: () => setState("authGateModal", null),
    },
    chatAccessDenied: {
      isModalOpen: () => state.chatAccessDeniedModal,
      openModal: () => setState("chatAccessDeniedModal", true),
      closeModal: () => setState("chatAccessDeniedModal", false),
    },
    disallowed: {
      isModalOpen: () => state.disallowedModal !== null,
      payload: () => state.disallowedModal,
      openModal: (opts: DisallowedModalPayload) => setState("disallowedModal", opts),
      closeModal: () => setState("disallowedModal", null),
    },
    projectSwitchConfirm: {
      isModalOpen: () => state.projectSwitchConfirmModal !== null,
      state: () => state.projectSwitchConfirmModal,
      openModal: (options: ProjectSwitchConfirmModalState) => setState("projectSwitchConfirmModal", options),
      closeModal: () => setState("projectSwitchConfirmModal", null),
    },
    projectSwitchStreaming: {
      isModalOpen: () => state.projectSwitchStreamingModal !== null,
      state: () => state.projectSwitchStreamingModal,
      openModal: (options: ProjectSwitchStreamingModalState) => setState("projectSwitchStreamingModal", options),
      closeModal: () => setState("projectSwitchStreamingModal", null),
    },
    aiLinkWarning: {
      isModalOpen: () => state.aiLinkWarningModal !== null,
      href: () => state.aiLinkWarningModal?.href ?? "",
      openModal: (href: string) => setState("aiLinkWarningModal", { href }),
      closeModal: () => setState("aiLinkWarningModal", null),
    },
  };
};
